import React from "react";
import "../../styles/ContractForm.scss";
import HelperText from "../helper-text/HelperText";
import ContractFormSchema from "../../schemas/ContractFormSchema";
import SearchInput from "../search-input/SearchInput";

const ContractForm = (props) => {
    const { contractFormData, currentUser } = props;
    const { contractForm: { register, setValue, formState, getValues, clearErrors }, serviceNumber } = props;
    const { gapContractNumber, contractLastName } = ContractFormSchema;
    const [VinSearchFlag, setVinSearchFlag] = React.useState(false);
    // PATU-68 (decision D1 / Reading B): lock the last-name field for ANY authenticated
    // non-lender (federated Contract Holder) session, regardless of whether the JWT carried a
    // lastName claim. With no lastName the field renders locked-and-empty — the holder cannot
    // type one and the missing claim is treated as an upstream IdP misconfiguration. Lenders
    // keep an editable field (broad search is not last-name-scoped, PATU-51).
    const isLastNameLocked = !!(currentUser?.authenticated && !currentUser?.isLender);

    React.useEffect(() => {
        const localStorageData = JSON.parse(localStorage.getItem("tpa_data"));
        if (localStorageData?.allow_17_chars_vin_search) {
            setVinSearchFlag(true);
        }
    }, [contractFormData])

    React.useEffect(() => {
        // PATU-68: only prefill when the JWT actually carried a last name. When the field is
        // locked but lastName is absent (Reading B), it stays locked-and-empty — and we must
        // not dereference a null lastName here.
        if (isLastNameLocked && currentUser?.lastName) {
            // PATU-70: the field is locked, so the holder cannot shorten an
            // over-length JWT last name. The lookup already matches on a 30-char
            // case-insensitive prefix (real SCS + mock), so truncate to the
            // field's 30-char max before validating — otherwise the untruncated
            // value fails the maxLength rule and the Find button stays disabled,
            // locking the holder out of their own contract.
            setValue('contractLastName', currentUser.lastName.toUpperCase().slice(0, 30), { shouldValidate: true });
        }
    }, [isLastNameLocked, currentUser?.lastName, setValue])

    const handleSetValue = (e) => {
        e.preventDefault();
        setValue(e.target.name, e.target.value.trim().toUpperCase(), { shouldValidate: true })
    }

    const formatPhoneNumber = (phoneNumberString) => {
        let cleaned = ('' + phoneNumberString).replace(/\D/g, '');
        const match = cleaned.match(/^(\d{1,3})(\d{0,3})(\d{0,4})(\d{0,4})$/);
        if (match) {
            cleaned = `(${match[1]}${match[2] ? ') ' : ''}${match[2]}${match[3] ? '-' : ''}${match[3]}${match[4] ? ' x' : ''}${match[4]}`;
        }
        return cleaned;
    }

    return (
        <React.Fragment>
            <div className="contract-heading">Locate Your Contract</div>
            <p className="contract-description">
                Verify your coverage by entering the following
                information as listed on your Contract.
            </p>
            <div className="mt-12">
                <SearchInput
                    label="Last Name or Business Name"
                    name="contractLastName"
                    isRequired={true}
                    fieldRegister={register('contractLastName', {
                        ...contractLastName,
                        onChange: (e) => {
                            e.preventDefault();
                            setValue(e.target.name, e.target.value.toUpperCase(), { shouldValidate: true })
                        }
                    })}
                    errorMessage={formState?.errors?.contractLastName?.message || null}
                    maxLength={30}
                    {...props}
                    disabled={isLastNameLocked}
                />
                <HelperText text="As found in your paperwork." isShownHelperIcon={false} />
            </div>

            <div className="mt-12">
                <h6 className="and-text"><span>and either</span></h6>
            </div>
            <div className="mt-12">
                <SearchInput
                    label="Contract Number"
                    name="gapContractNumber"
                    fieldRegister={register('gapContractNumber', {
                        ...gapContractNumber,
                        validate: {
                            required: (value) => {
                                clearErrors('vin');
                                clearErrors('gapContractNumber');
                                if (!value && !getValues('vin')) {
                                    return 'Contract Number or VIN Number is required.';
                                }
                                return true;
                            },
                        },
                        onChange: (e) => { handleSetValue(e) }
                    })}
                    errorMessage={(!getValues('vin') && formState?.errors?.gapContractNumber?.message) ? formState?.errors?.gapContractNumber?.message : null}
                    maxLength={25}
                    {...props}
                />
                <HelperText text={`As found in your paperwork or call us at ` + formatPhoneNumber(serviceNumber) + `.`} isShownHelperIcon={false} />
            </div>
            <div className="mt-12">
                <h6 className="and-text"><span>or</span></h6>
            </div>
            <div className="mt-12" data-testid="vin1">
                <SearchInput
                    label="VIN Number"
                    name="vin"
                    placeholder={VinSearchFlag ? "Enter the full 17 characters" : "Enter the last 6 or full 17 characters"}
                    fieldRegister={register('vin', {
                        validate: {
                            required: (value) => {
                                clearErrors('vin');
                                clearErrors('gapContractNumber');
                                if (!value && !getValues('gapContractNumber')) {
                                    return 'VIN Number or Contract Number is required.';
                                } else if (value && !getValues('gapContractNumber') && VinSearchFlag && value.length < 17) {
                                    return 'VIN should be 17 characters long.';
                                } else if (!VinSearchFlag && !getValues('gapContractNumber') && !ContractFormSchema.vin.pattern.value.test(value)) {
                                    return 'VIN should be either 6 or 17 characters long.';
                                }
                                return true;
                            },
                        },
                        onChange: (e) => { handleSetValue(e) }
                    })}
                    errorMessage={(!getValues('gapContractNumber') && formState?.errors?.vin?.type === 'required') ? formState?.errors?.vin?.message : formState?.errors?.vin?.message || null}
                    isRequired={false}
                    minLength={VinSearchFlag ? 17 : 6}
                    maxLength={17}
                    {...props}
                />
                <HelperText text="Find this in your paperwork or insurance card." isShownHelperIcon={false} />
            </div>
        </React.Fragment>
    )
};

export default ContractForm;